// The site's frame: the top bar (the wordmark with its Konark wheel, the way round the page, logging in, claiming a
// name) and the footer (the business details and every policy page, as payment providers require), shared by the
// homepage and the information pages.
import { useEffect, useState } from 'react';
import { BUSINESS, REG, SITE_NAME, unfilled } from './brand';
import { useAccount } from './account';
import { Wheel } from './landing/Wheel';
import { openRegister } from './Register';
import { linkProps } from './router';

export const POLICY_PAGES = [
  { path: '/about', label: 'About us' },
  { path: '/contact', label: 'Contact us' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/terms', label: 'Terms & conditions' },
  { path: '/privacy', label: 'Privacy policy' },
  { path: '/refunds', label: 'Cancellation & refunds' },
  { path: '/delivery', label: 'Shipping & delivery' },
];

export function Logo() {
  return (
    <a className="gx-logo" {...linkProps('/')} aria-label={`${SITE_NAME} home`}>
      <b>GG</b>
      <Wheel className="w" />
      <b>BHARAT</b>
    </a>
  );
}

/** a value from BUSINESS, highlighted while it still needs filling in */
export function Fill({ v }: { v: string }) {
  return unfilled(v) ? <mark className="gx-fill">{v.replace('FILL_IN ', '[')}]</mark> : <>{v}</>;
}

/** India's time, ticking */
export function useIst() {
  const fmt = () => new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
  const [t, setT] = useState(fmt);
  useEffect(() => {
    const i = setInterval(() => setT(fmt()), 1000);
    return () => clearInterval(i);
  }, []);
  return t;
}

export function SiteNav({ scroller }: { scroller?: React.RefObject<HTMLElement | null> }) {
  const me = useAccount();
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = scroller?.current;
    if (!el) return setSolid(true);
    const on = () => setSolid(el.scrollTop > 40);
    el.addEventListener('scroll', on, { passive: true });
    on();
    return () => el.removeEventListener('scroll', on);
  }, [scroller]);
  const links = [
    { href: '/#india', label: 'Bharat' },
    { href: '/#chandigarh', label: 'Chandigarh' },
    { href: '/#stage', label: 'Games · Shows · Events' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/#faq', label: 'FAQ' },
  ];
  return (
    <header className={`gx-nav${solid ? ' solid' : ''}${open ? ' open' : ''}`}>
      <Logo />
      <nav aria-label="Main">
        {links.map((l) => (
          <a key={l.href} className="txt" href={l.href} onClick={() => setOpen(false)} {...(l.href.startsWith('/#') ? {} : linkProps(l.href))}>
            {l.label}
          </a>
        ))}
        {me ? (
          <a className="txt you" {...linkProps('/account')} title="Your account">
            @{me.displayName}
          </a>
        ) : (
          <a className="txt" {...linkProps('/login')}>
            Log in
          </a>
        )}
        {!me?.claim && (
          <button
            type="button"
            className="gx-btn sm"
            onClick={() => {
              setOpen(false);
              openRegister();
            }}
          >
            Join the waitlist · ₹{REG.fee}
          </button>
        )}
      </nav>
      <button type="button" className="gx-burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <i />
        <i />
      </button>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="gx-foot">
      <div className="ridge" aria-hidden />
      <div className="top">
        <div>
          <Logo />
          <p className="dim">India’s worlds, built to play: games of real Indian cities, and shows and events inside them, in 3D, in your browser. First stop: Chandigarh, the City Beautiful.</p>
        </div>
        <nav aria-label="Policies">
          {POLICY_PAGES.map((p) => (
            <a key={p.path} {...linkProps(p.path)}>
              {p.label}
            </a>
          ))}
        </nav>
        <address>
          <b>
            <Fill v={BUSINESS.legalName} />
          </b>
          <span>
            <Fill v={BUSINESS.address} />
          </span>
          <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
          {!unfilled(BUSINESS.phone) && <span>{BUSINESS.phone}</span>}
        </address>
      </div>
      <p className="f-fine">
        © {new Date().getFullYear()} <Fill v={BUSINESS.legalName} /> · {SITE_NAME}. Map data © OpenStreetMap contributors (ODbL). Relief: NOAA ETOPO1 (public domain). Borders: Natural
        Earth (public domain), as India shows them. GG Bharat is an independent studio, not run by or affiliated with the Chandigarh Administration, the traffic police or any
        government body. All prices in Indian rupees (₹), taxes included.
      </p>
    </footer>
  );
}
